/**
 * TAK-2086: the Activity Plan's Financial Result, in one place — used by the
 * Activity Plan (Outputs card) and the Evaluation page.
 *
 * Income   = Program Contribution / Program Grant (linked activities)
 *          + Funding partners + Other income
 * Expenses = Venue Hire + Contractors + Other expenditure
 * Result   = Income − Expenses
 *
 * Projected amounts are used before green light, actual amounts after. On the
 * venue, contractor and partner rows `cost` is the projected amount and
 * `actualCost` the actual; a row added after green light whose actual hasn't
 * been set yet falls back to its projected amount. In-kind and Network
 * partners are never counted, nor is the parent program's own partner entry
 * on a linked activity (isFromProgramLevel) — it carries the same amount as
 * the Program Contribution, which is already counted. isFromProgramLevel sits
 * on the shared saved-partner record, so it only means "the parent program"
 * on a linked activity; elsewhere the partner counts like any other.
 *
 * The same rules live in takso-service src/utils/activity/financials.js —
 * keep the two in step.
 */

// work in whole cents so totals don't pick up floating-point noise
const toCents = (value) => Math.round(Number(value || 0) * 100);

const rowAmount = ({ row, actual }) => {
  if (actual && row.actualCost != null) {
    return toCents(row.actualCost);
  }
  return toCents(row.cost);
};

const isProgramLevelPartner = (row) =>
  Boolean(
    row.isFromProgramLevel || (row.partner && row.partner.isFromProgramLevel)
  );

// a linked activity carries its program as `parentActivity` (the activity
// payload the website gets) or `parentId` (a database row)
export const isLinkedActivity = (activity) =>
  Boolean(activity.parentId || activity.parentActivity);

const sumRows = ({ rows, actual, include = () => true }) =>
  (rows || [])
    .filter(include)
    .reduce((total, row) => total + rowAmount({ row, actual }), 0);

export const getActivityFinancials = ({ activity, actual = false }) => {
  const isLinked = isLinkedActivity(activity);

  const cents = {
    programContribution: isLinked
      ? toCents(
          actual ? activity.actualGrantAmount : activity.projectedGrantAmount
        )
      : 0,
    partnerFunding: sumRows({
      rows: activity.partners,
      actual,
      include: (partner) =>
        partner.supportType === 'FUNDING' &&
        !(isLinked && isProgramLevelPartner(partner)),
    }),
    otherIncome: toCents(
      actual ? activity.actualBudget : activity.projectedBudget
    ),
    venueHire: sumRows({ rows: activity.venues, actual }),
    contractors: sumRows({ rows: activity.contractors, actual }),
    otherExpenditure: toCents(
      actual ? activity.actualCost : activity.projectedCost
    ),
  };
  cents.income =
    cents.programContribution + cents.partnerFunding + cents.otherIncome;
  cents.expenses = cents.venueHire + cents.contractors + cents.otherExpenditure;
  cents.result = cents.income - cents.expenses;

  const result = {};
  Object.keys(cents).forEach((key) => {
    result[key] = cents[key] / 100;
  });
  return result;
};

// sActivityPlanType ids of programs (Series, Festival, Grants, Cross Sectoral,
// Multi Activity)
const PROGRAM_TYPE_IDS = [2, 3, 4, 5, 6];

/**
 * TAK-2086: whether an activity has reportable expenditure, for the
 * green-light / completion checks — "No reportable expenditure" ticked, or
 * expenses above zero. A single activity counts all its expenses (Venue
 * Hire, contractors, Other expenditure); a program still uses its own
 * expenditure figure.
 */
export const hasReportableExpenditure = ({ activity, actual = false }) => {
  if (activity.noExpenditure) {
    return true;
  }
  if (PROGRAM_TYPE_IDS.includes(Number(activity.activityPlanTypeId))) {
    return (
      Number((actual ? activity.actualCost : activity.projectedCost) || 0) > 0
    );
  }
  return getActivityFinancials({ activity, actual }).expenses > 0;
};

// sActivityPlanType: Single Activity
const SINGLE_ACTIVITY_TYPE_ID = 1;

/**
 * The Financial Result shown on the Activity Plan and the Evaluation page.
 * Programs keep their own formula (program amount + other income − other
 * expenditure) until their program-level pool is designed.
 */
export const financialResult = ({ activity, actual = false }) => {
  if (Number(activity.activityPlanTypeId) === SINGLE_ACTIVITY_TYPE_ID) {
    return getActivityFinancials({ activity, actual }).result;
  }
  const amount = (value) => Number(value || 0);
  const isLinked = isLinkedActivity(activity);
  return actual
    ? (isLinked ? amount(activity.actualGrantAmount) : 0) +
        amount(activity.actualBudget) -
        amount(activity.actualCost)
    : (isLinked ? amount(activity.projectedGrantAmount) : 0) +
        amount(activity.projectedBudget) -
        amount(activity.projectedCost);
};
